import { useEffect, useRef } from 'react';
import { uniq, compact, isNil } from 'lodash-es';
import type { Site } from '@hotjar/site';
import { useCurrentSite, useSites } from '@hotjar/site';
import { hotjar } from '@hotjar/tracking';
import type { CurrentUser } from '@hotjar/user';
import { useCurrentUser } from '@hotjar/user';
import { hjCookieConverter, hjGetCookie } from '../utils/cookies';
import type { FeatureGroup } from '../domains/revenue/plan/types';
import { FeatureGroupEnum } from '../domains/revenue/plan/types';
import { useTrafficCoverage } from './useTrafficCoverage';

const UTM_COOKIE_NAME = 'utm_params';

const getUtmSource = (): string | null => {
  const utmParamsEncoded = hjGetCookie(UTM_COOKIE_NAME);
  if (!utmParamsEncoded) {
    return null;
  }
  const utmParamsString = hjCookieConverter.decode(utmParamsEncoded);
  // Wrapped in try/catch in case cookie value is not valid JSON format
  try {
    const utmParamsDecoded = JSON.parse(utmParamsString);
    return utmParamsDecoded.utm_source;
  } catch {
    return null;
  }
};

const convertTimestampToISO8601 = (timestamp: number | null): string | null => {
  if (isNil(timestamp)) {
    return null;
  }
  return new Date(timestamp * 1000).toISOString();
};

/**
 * Helper function to get a flat list of unique industries, excluding nulls, sorted.
 */
const getDistinctIndustries = (sites: Site[]): string[] => {
  return compact(uniq(sites.map((item) => item.industry))).sort();
};

const getHighestPlan = (sites: Site[]): FeatureGroup => {
  const highest: FeatureGroupEnum = sites
    .map((item) => item.effectivePlan)
    .filter((item): item is FeatureGroup => item !== null)
    // Convert from the string to an enum ordinal such
    // that we can compare
    .map((item) => FeatureGroupEnum[item])
    // Find the highest plan, comparing enum ordinals
    .reduce((highestPlan, featureGroup) => {
      if (featureGroup > highestPlan) {
        return featureGroup;
      }
      return highestPlan;
    }, FeatureGroupEnum.basic);

  // Since we'll have the highest plan in the form of
  // an ordinal, reverse map back to the name of the
  // enum
  return FeatureGroupEnum[highest] as FeatureGroup;
};

/**
 * Helper function to get a flat list of unique features, sorted, for all the passed accounts.
 */
const getDistinctFeatures = (currentUser: CurrentUser) => {
  return uniq(
    (currentUser.accounts ?? [])
      .map((item) => item.features)
      .flat() // Features are arrays within the account object.
      .sort(),
  );
};

const getHighestSampleRate = (sites: Site[]) => {
  return Math.max(...sites.map((item) => item.sampleRate));
};

export const buildUserAttributes = (
  user: CurrentUser,
  sites: Site[],
  coveragePercentage: number,
): Record<string, unknown> => {
  const isOwner = user.accounts && user.accounts.length > 0;
  const industries = getDistinctIndustries(sites);
  const highestSampleRate = getHighestSampleRate(sites);
  const highestPlan = getHighestPlan(sites);
  const features = getDistinctFeatures(user);

  const attributes = {
    account_feature_flags: features.length > 0 ? features.join(',') : null,
    became_a_customer: convertTimestampToISO8601(user.becameACustomer),
    country: user.country ?? null,
    current_site_coverage: coveragePercentage,
    highest_plan: highestPlan,
    highest_sample_rate: highestSampleRate,
    owned_account_became_a_customer: isOwner
      ? convertTimestampToISO8601(user.becameACustomer)
      : null,
    owned_account_billing_cycle: isOwner ? user.accountBillingCycle : null,
    referrer_url: getUtmSource(),
    signed_up: convertTimestampToISO8601(user.created),
    site_industry: industries.length > 0 ? industries.join(',') : null,
    user_role: user.role ?? null,
  };
  return attributes;
};

export const useIdentifyOnce = (): void => {
  const isIdentifyCalledRef = useRef(false);
  const {
    query: { data: sites, isPlaceholderData: isPlaceholderDataSites },
  } = useSites();
  const { data: user } = useCurrentUser();
  const { data: currentSite, isPlaceholderData: isPlaceholderDataCurrentSite } = useCurrentSite();
  const { coveragePercentage } = useTrafficCoverage();

  useEffect(() => {
    if (
      !isIdentifyCalledRef.current &&
      hotjar.isAvailable() &&
      user &&
      sites &&
      currentSite &&
      !isPlaceholderDataSites &&
      !isPlaceholderDataCurrentSite
    ) {
      isIdentifyCalledRef.current = true;
      const attributes = buildUserAttributes(user, sites, coveragePercentage);
      hotjar.identify(user.id, attributes);

      hotjar.sessionEvent({
        user_id: user.id,
        account_id: currentSite.account?.id,
      });
    }
  }, [
    coveragePercentage,
    currentSite,
    isPlaceholderDataCurrentSite,
    isPlaceholderDataSites,
    sites,
    user,
  ]);
};
